import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  exchangeCodeForTokens,
  refreshAccessToken,
  decodeTokenClaims,
  isTokenExpired,
  buildLogoutUrl,
} from '@/services/auth.service';

const ACCESS_TOKEN_KEY = 'church_admin_access_token';
const REFRESH_TOKEN_KEY = 'church_admin_refresh_token';
const ID_TOKEN_KEY = 'church_admin_id_token';

export interface AuthUser {
  sub: string;
  email: string;
  givenName?: string;
  familyName?: string;
}

export const useAuthStore = defineStore('auth', () => {
  // Tokens are stored in sessionStorage (cleared on tab close)
  const accessToken = ref<string | null>(sessionStorage.getItem(ACCESS_TOKEN_KEY));
  const refreshToken = ref<string | null>(sessionStorage.getItem(REFRESH_TOKEN_KEY));
  const idToken = ref<string | null>(sessionStorage.getItem(ID_TOKEN_KEY));
  const user = ref<AuthUser | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Hydrate user from stored id token
  if (idToken.value) {
    try {
      const claims = decodeTokenClaims(idToken.value);
      if (!isTokenExpired(claims)) {
        user.value = {
          sub: claims.sub,
          email: claims.email,
          givenName: claims.given_name,
          familyName: claims.family_name,
        };
      }
    } catch {
      // Invalid stored token — clear it
      sessionStorage.removeItem(ID_TOKEN_KEY);
      idToken.value = null;
    }
  }

  const isAuthenticated = computed(
    () => accessToken.value !== null && user.value !== null,
  );

  async function handleCallback(code: string, state: string): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const tokens = await exchangeCodeForTokens(code, state);
      const claims = decodeTokenClaims(tokens.id_token);

      accessToken.value = tokens.access_token;
      refreshToken.value = tokens.refresh_token;
      idToken.value = tokens.id_token;

      sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);

      user.value = {
        sub: claims.sub,
        email: claims.email,
        givenName: claims.given_name,
        familyName: claims.family_name,
      };
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Authentication failed';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function tryRefreshToken(): Promise<boolean> {
    if (!refreshToken.value) return false;
    try {
      const tokens = await refreshAccessToken(refreshToken.value);
      const claims = decodeTokenClaims(tokens.id_token);

      accessToken.value = tokens.access_token;
      idToken.value = tokens.id_token;

      sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);

      user.value = {
        sub: claims.sub,
        email: claims.email,
        givenName: claims.given_name,
        familyName: claims.family_name,
      };
      return true;
    } catch {
      signOut();
      return false;
    }
  }

  function signOut(): void {
    accessToken.value = null;
    refreshToken.value = null;
    idToken.value = null;
    user.value = null;
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(ID_TOKEN_KEY);
    window.location.href = buildLogoutUrl();
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    isLoading,
    error,
    handleCallback,
    tryRefreshToken,
    signOut,
  };
});
